/** Locations Room. Map-linked pins + high-bandwidth gallery link
 *  (delegated to the shared media vault above) + permit toggle. */
import { useState } from "react";
import { Plus, Trash2, MapPin, ExternalLink, CheckCircle2, XCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import useCubicleRecords from "./useCubicleRecords";

const PERMITS = [
  { key: "pending",  label: "Pending",  Icon: Clock,        color: "#F5B82E" },
  { key: "approved", label: "Approved", Icon: CheckCircle2, color: "#3ED67C" },
  { key: "rejected", label: "Rejected", Icon: XCircle,      color: "#E5484D" },
];

export default function LocationsCubicle({ projectId }) {
  const locations = useCubicleRecords(projectId, "locations", "location");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({
    name: "", address: "", lat: "", lng: "", notes: "", permit_status: "pending",
  });

  return (
    <div className="space-y-6" data-testid="locations-cubicle">
      <section className="cs-card rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
              Location Pins
            </div>
            <div className="font-display text-lg uppercase tracking-tight">
              {locations.items.length} scouted
            </div>
          </div>
          <Button onClick={() => setDialog(true)}
                  className="h-8 gap-1 bg-white text-black hover:bg-white/90"
                  data-testid="location-add">
            <Plus size={13}/> Pin
          </Button>
        </div>

        {locations.items.length === 0 ? (
          <div className="text-sm text-white/45 py-8 text-center">
            No locations yet. Drop pins with coordinates or an address —
            each row links directly to Google Maps.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="location-list">
            {locations.items.map((r) => {
              const d = r.data || {};
              const status = PERMITS.find((p) => p.key === (d.permit_status || "pending"));
              const gm = d.lat && d.lng
                ? `https://maps.google.com/?q=${d.lat},${d.lng}`
                : d.address ? `https://maps.google.com/?q=${encodeURIComponent(d.address)}` : null;
              return (
                <div key={r.id}
                     className="rounded-xl border border-white/10 bg-black/30 p-4"
                     data-testid={`location-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white truncate">{d.name || "Untitled"}</div>
                      {d.address && <div className="text-xs text-white/60 mt-0.5">{d.address}</div>}
                      {(d.lat || d.lng) && (
                        <div className="text-[11px] font-mono text-white/45 mt-0.5">
                          {d.lat}, {d.lng}
                        </div>
                      )}
                    </div>
                    <Button variant="ghost" onClick={() => locations.remove(r.id)}
                            className="h-7 px-2 text-white/45 hover:text-rose-300"
                            data-testid={`location-remove-${r.id}`}>
                      <Trash2 size={13}/>
                    </Button>
                  </div>

                  {d.notes && <div className="text-xs text-white/60 mt-2">{d.notes}</div>}

                  {/* Permit toggle */}
                  <div className="mt-3 flex items-center gap-1.5" data-testid={`location-permit-${r.id}`}>
                    {PERMITS.map((p) => {
                      const isActive = (d.permit_status || "pending") === p.key;
                      return (
                        <button key={p.key} type="button"
                                onClick={() => locations.patch(r.id, { permit_status: p.key })}
                                className={"h-7 px-2 rounded-md border text-[10px] tracking-[0.18em] uppercase inline-flex items-center gap-1 " +
                                           (isActive ? "text-black" : "text-white/60 border-white/15 hover:text-white")}
                                style={isActive ? { background: p.color, borderColor: p.color } : {}}
                                data-testid={`location-permit-${r.id}-${p.key}`}>
                          <p.Icon size={11}/>{p.label}
                        </button>
                      );
                    })}
                  </div>
                  {gm && (
                    <a href={gm} target="_blank" rel="noreferrer"
                       className="mt-2 inline-flex items-center gap-1 text-[11px] text-emerald-300 hover:text-emerald-200">
                      <MapPin size={11}/> Open in maps <ExternalLink size={9}/>
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg bg-neutral-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-tight">Add Location</DialogTitle>
            <DialogDescription className="sr-only">New scouted location.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"
                     data-testid="location-name-input"/></div>
            <div className="col-span-2"><Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"/></div>
            <div><Label>Latitude</Label>
              <Input value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"/></div>
            <div><Label>Longitude</Label>
              <Input value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"/></div>
            <div className="col-span-2"><Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        className="mt-1 bg-black/40 border-white/15"/></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={async () => {
              if (!form.name.trim()) return;
              await locations.create("location", {
                ...form,
                lat: form.lat ? Number(form.lat) : "",
                lng: form.lng ? Number(form.lng) : "",
              });
              setDialog(false);
              setForm({ name: "", address: "", lat: "", lng: "", notes: "", permit_status: "pending" });
            }} className="bg-white text-black hover:bg-white/90"
               data-testid="location-submit">Save location</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
